/** @jsx jsx */
import {
  Card,
  FileEntryIconsProvider,
  Flex,
  Modal,
  ModalContainer,
  ModalContent,
  ModalHeader,
  ModalHeading,
  ModalProps,
  Paragraph,
} from "@eworkit/iris";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { IAttachment } from "@/data/attachments";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { useFormats } from "@/hooks/Formats";

interface ChooseExistingAttachmentModalProps extends ModalProps {
  profile: IConsultantProfile;
  onAttachmentClick: (attachment: IAttachment) => void;
}

export const ChooseExistingAttachmentModal: FC<ChooseExistingAttachmentModalProps> = ({
  profile,
  onAttachmentClick,
  ...modalProps
}) => {
  const { t } = useTranslation("session");

  const { formatDate } = useFormats();

  const header = t("JOB_REQUESTS.APPLY.WIZARD.CHOOSE_ATTACHMENTS_FROM_PROFILE");

  const handleCardClick = useCallback(
    (attachment: IAttachment) => {
      onAttachmentClick(attachment);
      modalProps.dialogState.hide();
    },
    [onAttachmentClick, modalProps],
  );

  return (
    <Modal aria-label={header} {...modalProps}>
      <ModalContainer>
        <ModalHeader
          onCloseClick={modalProps.dialogState.hide}
          closeButtonAriaLabel={t("COMPONENTS.MODAL.CLOSE")}
          headerPlaceholder={<ModalHeading>{header}</ModalHeading>}
        />

        <ModalContent>
          <Flex flexDirection="column" gap="xs" pb="sm">
            {profile.attachments
              .sort((a, b) => new Date(b.createdDate!).getTime() - new Date(a.createdDate!).getTime())
              .map(attachment => (
                <Card
                  key={attachment.id}
                  onClick={() => handleCardClick(attachment)}
                  sx={{
                    px: ["sm", "sm", "sm", "sm"],
                    py: ["xs", "xs", "xs", "xs"],
                  }}>
                  <Flex alignItems="center" gap="xs">
                    <FileEntryIconsProvider size="md" color="n700" name={attachment.name} />

                    <Flex flexDirection="column">
                      <Paragraph ellipsis>{attachment.name}</Paragraph>
                      <Paragraph size="p-sm" color="n700">
                        {formatDate(attachment.createdDate)}
                      </Paragraph>
                    </Flex>
                  </Flex>
                </Card>
              ))}
          </Flex>
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
};
